import { useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Ticket, ChevronLeft, ChevronRight, Plus, Pencil, Trash2 } from "lucide-react";
import {
  useAdminCouponsQuery,
  useAdminCreateCouponMutation,
  useAdminUpdateCouponMutation,
  useAdminDeleteCouponMutation,
  AdminCoupon,
} from "@/generated/graphql";
import { AdminUserPicker, type AdminUserOption } from "@/components/admin/AdminUserPicker";
import { formatDateTime } from "@/lib/format";
import { useToast } from "@/hooks/use-toast";

interface CouponFormData {
  code: string;
  type: string;
  quantity: number;
  month_cycles: number;
  month_discount: number | null;
  year_cycles: number | null;
  year_discount: number | null;
  expiry_date: string;
  assigned_user: AdminUserOption | null;
}

const initialFormData: CouponFormData = {
  code: "",
  type: "percentage",
  quantity: 100,
  month_cycles: 1,
  month_discount: 10,
  year_cycles: null,
  year_discount: null,
  expiry_date: "",
  assigned_user: null,
};

const AdminCoupons = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<AdminCoupon | null>(null);
  const [formData, setFormData] = useState<CouponFormData>(initialFormData);

  const { data, loading, error, refetch } = useAdminCouponsQuery({
    variables: {
      page,
      pageSize,
      search: search || undefined,
    },
    fetchPolicy: "cache-and-network",
  });

  const [createCoupon, { loading: creating }] = useAdminCreateCouponMutation();
  const [updateCoupon, { loading: updating }] = useAdminUpdateCouponMutation();
  const [deleteCoupon, { loading: deleting }] = useAdminDeleteCouponMutation();

  const coupons = data?.adminCoupons?.data || [];
  const totalPages = data?.adminCoupons?.pageCount || 1;
  const total = data?.adminCoupons?.total || 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  const handleInputChange = (field: keyof CouponFormData, value: string | number | null | AdminUserOption) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setSelectedCoupon(null);
  };

  const openCreateModal = () => {
    resetForm();
    setIsCreateModalOpen(true);
  };

  const openEditModal = (coupon: AdminCoupon) => {
    setSelectedCoupon(coupon);
    setFormData({
      code: coupon.code,
      type: coupon.type,
      quantity: coupon.quantity,
      month_cycles: coupon.month_cycles,
      month_discount: coupon.month_discount ?? null,
      year_cycles: coupon.year_cycles ?? null,
      year_discount: coupon.year_discount ?? null,
      expiry_date: coupon.expiry_date || "",
      assigned_user: coupon.assigned_user
        ? {
            id: coupon.assigned_user.id,
            name: coupon.assigned_user.name ?? null,
            email: coupon.assigned_user.email,
          }
        : null,
    });
    setIsEditModalOpen(true);
  };

  const openDeleteModal = (coupon: AdminCoupon) => {
    setSelectedCoupon(coupon);
    setIsDeleteModalOpen(true);
  };

  // Known backend codes map to localized copy; raw backend text is never shown.
  const couponErrorTitle = (err: unknown, fallback: string) =>
    err instanceof Error && err.message.includes("assigned_user_not_found")
      ? t("admin.coupons.errors.assigned_user_not_found")
      : fallback;

  const handleCreate = async () => {
    try {
      await createCoupon({
        variables: {
          input: {
            code: formData.code,
            type: formData.type,
            quantity: formData.quantity,
            month_cycles: formData.month_cycles,
            month_discount: formData.month_discount,
            year_cycles: formData.year_cycles,
            year_discount: formData.year_discount,
            expiry_date: formData.expiry_date || undefined,
            ...(formData.assigned_user ? { assignedUserId: formData.assigned_user.id } : {}),
          },
        },
      });

      toast({
        title: t("admin.coupons.toast.created"),
      });

      setIsCreateModalOpen(false);
      resetForm();
      refetch();
    } catch (err) {
      console.error("Failed to create coupon:", err);
      toast({
        title: couponErrorTitle(err, t("admin.coupons.toast.createError")),
        variant: "destructive",
      });
    }
  };

  const handleUpdate = async () => {
    if (!selectedCoupon) return;

    // Omitted = unchanged, null = clear, id = set.
    const previousId = selectedCoupon.assigned_user?.id ?? null;
    const nextId = formData.assigned_user?.id ?? null;
    const assignedUserPatch = nextId === previousId ? {} : { assignedUserId: nextId };

    try {
      await updateCoupon({
        variables: {
          id: selectedCoupon.documentId,
          input: {
            code: formData.code,
            type: formData.type,
            quantity: formData.quantity,
            month_cycles: formData.month_cycles,
            month_discount: formData.month_discount,
            year_cycles: formData.year_cycles,
            year_discount: formData.year_discount,
            expiry_date: formData.expiry_date || undefined,
            ...assignedUserPatch,
          },
        },
      });

      toast({
        title: t("admin.coupons.toast.updated"),
      });

      setIsEditModalOpen(false);
      resetForm();
      refetch();
    } catch (err) {
      console.error("Failed to update coupon:", err);
      toast({
        title: couponErrorTitle(err, t("admin.coupons.toast.updateError")),
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!selectedCoupon) return;

    try {
      await deleteCoupon({
        variables: {
          id: selectedCoupon.documentId,
        },
      });

      toast({
        title: t("admin.coupons.toast.deleted"),
      });

      setIsDeleteModalOpen(false);
      setSelectedCoupon(null);
      refetch();
    } catch (err) {
      console.error("Failed to delete coupon:", err);
      toast({
        title: t("admin.coupons.toast.deleteError"),
        variant: "destructive",
      });
    }
  };

  const isExpired = (expiryDate: string | null | undefined) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const formatDiscount = (type: string, discount: number | null | undefined) => {
    if (discount === null || discount === undefined) return "-";
    return type === "percentage" ? `${discount}%` : `R$ ${discount}`;
  };

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Ticket className="w-8 h-8 text-primary" />
              <div>
                <h1 className="text-2xl font-bold">{t("admin.coupons.title")}</h1>
                <p className="text-muted-foreground">{t("admin.coupons.subtitle")}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-red-500 border-red-500">
                ADMIN
              </Badge>
              <Button onClick={openCreateModal}>
                <Plus className="w-4 h-4 mr-2" />
                {t("admin.coupons.createCoupon")}
              </Button>
            </div>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t("admin.coupons.list")}</CardTitle>
                <span className="text-sm text-muted-foreground">
                  {t("admin.common.total")}: {total}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="flex gap-2 mb-4">
                <Input
                  placeholder={t("admin.coupons.searchPlaceholder")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="max-w-sm"
                />
                <Button type="submit" variant="outline">
                  <Search className="w-4 h-4" />
                </Button>
              </form>

              {loading && !data ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-500">{t("admin.common.error")}</div>
              ) : (
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("admin.coupons.code")}</TableHead>
                          <TableHead>{t("admin.coupons.type")}</TableHead>
                          <TableHead>{t("admin.coupons.monthDiscount")}</TableHead>
                          <TableHead>{t("admin.coupons.yearDiscount")}</TableHead>
                          <TableHead>{t("admin.coupons.quantity")}</TableHead>
                          <TableHead>{t("admin.coupons.expiryDate")}</TableHead>
                          <TableHead>{t("admin.coupons.createdAt")}</TableHead>
                          <TableHead className="w-[100px]">{t("admin.coupons.actions")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {coupons.map((coupon) => (
                          <TableRow key={coupon.id}>
                            <TableCell>
                              <code className="bg-muted px-2 py-1 rounded text-sm font-mono">
                                {coupon.code}
                              </code>
                              {coupon.assigned_user && (
                                <Badge variant="outline" className="ml-2 text-xs">
                                  {t("admin.coupons.assignedUserBadge", {
                                    user: coupon.assigned_user.name || coupon.assigned_user.email,
                                  })}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {coupon.type === "percentage" ? "%" : "R$"}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {formatDiscount(coupon.type, coupon.month_discount)}
                              {coupon.month_cycles === -1 && <span> ({t('checkout.discountForever')})</span>}
                              {coupon.month_cycles > 1 && (
                                <span className="text-muted-foreground text-xs ml-1">
                                  ({coupon.month_cycles}x)
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              {formatDiscount(coupon.type, coupon.year_discount)}
                              {coupon.year_cycles === -1 && <span> ({t('checkout.discountForever')})</span>}
                              {coupon.year_cycles && coupon.year_cycles > 1 && (
                                <span className="text-muted-foreground text-xs ml-1">
                                  ({coupon.year_cycles}x)
                                </span>
                              )}
                            </TableCell>
                            <TableCell>{coupon.quantity}</TableCell>
                            <TableCell>
                              {coupon.expiry_date ? (
                                <span
                                  className={
                                    isExpired(coupon.expiry_date) ? "text-red-500" : ""
                                  }
                                >
                                  {new Date(coupon.expiry_date).toLocaleDateString('pt-BR')}
                                  {isExpired(coupon.expiry_date) && (
                                    <Badge
                                      variant="outline"
                                      className="ml-2 text-red-500 border-red-500 text-xs"
                                    >
                                      {t("admin.coupons.expired")}
                                    </Badge>
                                  )}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell>{formatDateTime(coupon.createdAt)}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  data-testid={`edit-coupon-${coupon.id}`}
                                  onClick={() => openEditModal(coupon)}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  data-testid={`delete-coupon-${coupon.id}`}
                                  onClick={() => openDeleteModal(coupon)}
                                  className="text-red-500 hover:text-red-600"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {coupons.length === 0 && (
                          <TableRow>
                            <TableCell
                              colSpan={8}
                              className="text-center py-8 text-muted-foreground"
                            >
                              {t("admin.common.noResults")}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-sm text-muted-foreground">
                      {t("admin.common.page")} {page} {t("admin.common.of")} {totalPages}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Create Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("admin.coupons.createCoupon")}</DialogTitle>
            <DialogDescription>{t("admin.coupons.createCouponDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-code">{t("admin.coupons.code")}</Label>
                <Input
                  id="create-code"
                  value={formData.code}
                  onChange={(e) => handleInputChange("code", e.target.value.toUpperCase())}
                  placeholder="DISCOUNT20"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-type">{t("admin.coupons.type")}</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => handleInputChange("type", value)}
                >
                  <SelectTrigger id="create-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">{t("admin.coupons.typePercentage")}</SelectItem>
                    <SelectItem value="flat">{t("admin.coupons.typeFlat")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-quantity">{t("admin.coupons.quantity")}</Label>
                <Input
                  id="create-quantity"
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={(e) => handleInputChange("quantity", parseInt(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-expiry_date">{t("admin.coupons.expiryDate")}</Label>
                <Input
                  id="create-expiry_date"
                  type="date"
                  value={formData.expiry_date}
                  onChange={(e) => handleInputChange("expiry_date", e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="create-assigned-user">{t("admin.coupons.assignedUser")}</Label>
              <div className="flex items-center gap-2">
                <AdminUserPicker
                  id="create-assigned-user"
                  value={formData.assigned_user}
                  onChange={(user) => handleInputChange("assigned_user", user)}
                  placeholder={t("admin.coupons.assignedUserPlaceholder")}
                />
                {formData.assigned_user && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="shrink-0"
                    onClick={() => handleInputChange("assigned_user", null)}
                  >
                    {t("admin.coupons.assignedUserClear")}
                  </Button>
                )}
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">{t("admin.coupons.monthlySettings")}</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="create-month_discount">{t("admin.coupons.monthDiscount")}</Label>
                  <Input
                    id="create-month_discount"
                    type="number"
                    min="0"
                    value={formData.month_discount ?? ""}
                    onChange={(e) =>
                      handleInputChange("month_discount", e.target.value ? parseInt(e.target.value) : null)
                    }
                    placeholder={formData.type === "percentage" ? "10" : "50"}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-month_cycles">{t("admin.coupons.monthCycles")}</Label>
                  <Input
                    id="create-month_cycles"
                    type="number"
                    min="-1"
                    value={formData.month_cycles}
                    onChange={(e) => handleInputChange("month_cycles", parseInt(e.target.value) || 1)}
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">{t("admin.coupons.yearlySettings")}</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="create-year_discount">{t("admin.coupons.yearDiscount")}</Label>
                  <Input
                    id="create-year_discount"
                    type="number"
                    min="0"
                    value={formData.year_discount ?? ""}
                    onChange={(e) =>
                      handleInputChange("year_discount", e.target.value ? parseInt(e.target.value) : null)
                    }
                    placeholder={formData.type === "percentage" ? "20" : "100"}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-year_cycles">{t("admin.coupons.yearCycles")}</Label>
                  <Input
                    id="create-year_cycles"
                    type="number"
                    min="-1"
                    value={formData.year_cycles ?? ""}
                    onChange={(e) =>
                      handleInputChange("year_cycles", e.target.value ? parseInt(e.target.value) : null)
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleCreate} disabled={creating || !formData.code}>
              {creating ? t("admin.coupons.creating") : t("common.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("admin.coupons.editCoupon")}</DialogTitle>
            <DialogDescription>{t("admin.coupons.editCouponDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-code">{t("admin.coupons.code")}</Label>
                <Input
                  id="edit-code"
                  value={formData.code}
                  onChange={(e) => handleInputChange("code", e.target.value.toUpperCase())}
                  placeholder="DISCOUNT20"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-type">{t("admin.coupons.type")}</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => handleInputChange("type", value)}
                >
                  <SelectTrigger id="edit-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">{t("admin.coupons.typePercentage")}</SelectItem>
                    <SelectItem value="flat">{t("admin.coupons.typeFlat")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-quantity">{t("admin.coupons.quantity")}</Label>
                <Input
                  id="edit-quantity"
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={(e) => handleInputChange("quantity", parseInt(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-expiry_date">{t("admin.coupons.expiryDate")}</Label>
                <Input
                  id="edit-expiry_date"
                  type="date"
                  value={formData.expiry_date}
                  onChange={(e) => handleInputChange("expiry_date", e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-assigned-user">{t("admin.coupons.assignedUser")}</Label>
              <div className="flex items-center gap-2">
                <AdminUserPicker
                  id="edit-assigned-user"
                  value={formData.assigned_user}
                  onChange={(user) => handleInputChange("assigned_user", user)}
                  placeholder={t("admin.coupons.assignedUserPlaceholder")}
                />
                {formData.assigned_user && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="shrink-0"
                    onClick={() => handleInputChange("assigned_user", null)}
                  >
                    {t("admin.coupons.assignedUserClear")}
                  </Button>
                )}
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">{t("admin.coupons.monthlySettings")}</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-month_discount">{t("admin.coupons.monthDiscount")}</Label>
                  <Input
                    id="edit-month_discount"
                    type="number"
                    min="0"
                    value={formData.month_discount ?? ""}
                    onChange={(e) =>
                      handleInputChange("month_discount", e.target.value ? parseInt(e.target.value) : null)
                    }
                    placeholder={formData.type === "percentage" ? "10" : "50"}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-month_cycles">{t("admin.coupons.monthCycles")}</Label>
                  <Input
                    id="edit-month_cycles"
                    type="number"
                    min="-1"
                    value={formData.month_cycles}
                    onChange={(e) => handleInputChange("month_cycles", parseInt(e.target.value) || 1)}
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">{t("admin.coupons.yearlySettings")}</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-year_discount">{t("admin.coupons.yearDiscount")}</Label>
                  <Input
                    id="edit-year_discount"
                    type="number"
                    min="0"
                    value={formData.year_discount ?? ""}
                    onChange={(e) =>
                      handleInputChange("year_discount", e.target.value ? parseInt(e.target.value) : null)
                    }
                    placeholder={formData.type === "percentage" ? "20" : "100"}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-year_cycles">{t("admin.coupons.yearCycles")}</Label>
                  <Input
                    id="edit-year_cycles"
                    type="number"
                    min="-1"
                    value={formData.year_cycles ?? ""}
                    onChange={(e) =>
                      handleInputChange("year_cycles", e.target.value ? parseInt(e.target.value) : null)
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleUpdate} disabled={updating || !formData.code}>
              {updating ? t("admin.coupons.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.coupons.deleteCoupon")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.coupons.deleteConfirm", { code: selectedCoupon?.code })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600"
            >
              {deleting ? t("admin.coupons.deleting") : t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default AdminCoupons;
